/**
 * Shared helpers for reading/writing multi-value URL search parameters
 * used across enterprise report pages (e.g. providers[], locations[]).
 */

/** Deduplicate and sort an array of strings. */
export function uniqueSorted(arr: string[]): string[] {
  return Array.from(new Set(arr)).sort();
}

/**
 * Read a multi-value query parameter from URLSearchParams.
 *
 * Handles three encoding styles:
 *  - `key[]=a&key[]=b`  (bracket-style, from Inertia/Rails)
 *  - `key=a&key=b`      (repeated key)
 *  - `key=a,b`          (comma-separated)
 *  - `key=_none`        (explicit empty selection)
 *
 * Returns `null` when the key is absent (meaning "no filter applied").
 */
export function normalizeMulti(sp: URLSearchParams, key: string): string[] | null {
  const withBrackets = sp.getAll(`${key}[]`);
  const withoutBrackets = sp.getAll(key);
  if (withoutBrackets.length === 1 && withoutBrackets[0] === "_none") return [];
  if (withBrackets.length > 0) return uniqueSorted(withBrackets);
  if (withoutBrackets.length > 1) return uniqueSorted(withoutBrackets);
  if (withoutBrackets.length === 1) {
    const only = withoutBrackets[0]!;
    return uniqueSorted(only.split(",").filter(Boolean));
  }
  return null;
}

/**
 * Write a multi-value parameter onto a URL.
 *
 * `null` means "don't set anything" (parameter omitted).
 * An empty array encodes as `key=_none`.
 */
export function applyMulti(url: URL, key: string, vals: string[] | null): void {
  if (vals === null) return;
  if (vals.length === 0) {
    url.searchParams.set(key, "_none");
  } else {
    for (const v of vals) url.searchParams.append(`${key}[]`, v);
  }
}
